import React, { FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '../components';
import { FormInput } from '../components/forms/FormInput';
import { PasswordInput } from '../components/forms/PasswordInput';
import { RequestPasswordReset } from '../components/buttons/PasswordReset';
import { PatientDoc } from '../types/Patient/PatientDoc';
import usePatientDoc from '../hooks/usePatientDoc';
import { createPassagePatient } from '../data/createPassagePatient';
import * as Sentry from '@sentry/browser';
import { ButtonGreen } from '../components/buttons';

export const Route = createFileRoute('/login')({
  component: Login,
});

function Login() {
  // LOCAL STATE FOR USERNAME AND PASSWORD
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [useEmailPassword, setUseEmailPassword] = React.useState(false);
  const [password, setPassword] = React.useState('');
  const [working, setWorking] = React.useState(false);

  // AUTH HANDLING
  const { currentUser, firebaseLogin, firebaseSignInWithRedirect } = useAuth();
  const patientDocQuery = usePatientDoc(currentUser?.uid ? currentUser.uid : '123');
  const patientDocData = patientDocQuery.data as PatientDoc;
  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await firebaseLogin(email, password);
    } catch (error) {
      alert(
        "We couldn't log you in. Please try again. Or if you're having trouble, please reach out to info@mideohealth.com.",
      );
      setWorking(false);
    }
  };

  // NAVIGATION FROM TANSTACK ROUTER
  const navigate = useNavigate();

  // LISTEN FOR COMPONENT UPDATE
  React.useEffect(() => {
    // HANDLE REDIRECT TO REQUESTED URL
    const requestedURL = window.localStorage.getItem('requestedURL');
    if (currentUser && patientDocData && requestedURL) {
      // ACCOUNT FOR DIFFERENCE BETWEEN PROD AND STAGING URLS
      const path = requestedURL.includes('com') ? requestedURL.split('com')[1] : requestedURL.split('app')[1];
      setMessage('Navigating to Requested Page');
      navigate({
        to: path,
        from: '/login',
      });
    } else if (currentUser && patientDocData && !requestedURL) {
      setMessage('Your Home Page is Loading');
      navigate({
        to: '/home',
        from: '/login',
      });
    }

    if (currentUser && !patientDocData) {
      setMessage('Creating Your Account');
      //CREATE PATIENT FIRESTORE DOCUMENT
      createPassagePatient({
        uid: currentUser.uid,
        email: currentUser.email ? currentUser.email : '',
      }).catch((error) => {
        Sentry.captureException(error);
        console.error('Error creating patient doc', error);
        alert('Error creating patient document. Please contact support.');
      });
    }
  }, [currentUser, patientDocData, navigate]);

  return (
    <>
      {message.length > 0 ? (
        <div className="flex h-screen flex-col items-center justify-center">
          <img
            src="https://storage.googleapis.com/mideoid-console-public/logos/MIDEOhealth.png"
            alt="mideo_logo"
            className="h-36"
          />
          <p>{message}</p>
        </div>
      ) : (
        <div className="mt-36 flex h-screen flex-col items-center">
          <img
            src="https://storage.googleapis.com/mideoid-console-public/logos/MIDEOhealth.png"
            alt="mideo_logo"
            className="h-36"
          />
          <div className="mx-6 my-4 flex flex-col items-center">
            <ButtonGreen
              id="sign-in"
              type="button"
              onClick={async () => await firebaseSignInWithRedirect()}
              textContent="MIDEO Patients Login/Create Account"
              classNameOverride="my-4 rounded-md bg-sky-700 px-4 py-2 text-3xl text-white hover:bg-sky-800 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-500 disabled:shadow-none sm:px-8 sm:py-3"
            />
            <p className="text-center text-3xl italic">
              New and returning patients should click the above button and use their personal email to sign-in or create
              an account after reading the instructions{' '}
              <a className="font-bold text-blue-600 underline" href="/passwordless">
                here
              </a>
              .
            </p>
          </div>

          <div className="mx-6 my-4 flex flex-col items-center">
            <Button
              id="enroll"
              type="button"
              onClick={async () =>
                navigate({
                  to: '/enroll',
                  from: '/login',
                })
              }
              textContent="Partner Login"
            />
            <p className="text-center text-xl italic">
              Patients redeeming a benefit from their workplace or organization should click the above button and their
              company email and provided password to login.
            </p>
          </div>

          <div className="mx-6 my-4 flex flex-col items-center">
            <Button
              id="use-email-password"
              type="button"
              onClick={() => setUseEmailPassword(!useEmailPassword)}
              textContent="Legacy Login"
            />
            <p className="text-center text-xl italic">
              Patients who joined the practice prior to 2023 should click the above button.
            </p>
            <p className="text-center text-xl italic">(this is very rare).</p>
            {useEmailPassword && (
              <div className="flex h-screen flex-col items-center">
                <form>
                  <FormInput
                    id="username"
                    handleBlur={() => {}}
                    label="Email"
                    name="username"
                    value={email}
                    handleChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                  />
                  <PasswordInput
                    id="password"
                    title="Password"
                    password={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <div className="flex flex-col">
                    <Button
                      id="submit"
                      type="submit"
                      onClick={(e) => handleLogin(e)}
                      textContent="Login"
                      disabled={working}
                      working={working}
                      workingText="Logging in..."
                    />
                    <RequestPasswordReset email={email} />
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
